import React, { useState } from 'react';
import QuestionFlow from './components/QuestionFlow';
import RecommendationDisplay from './components/RecommendationDisplay';
import { AuthWrapper } from './components/auth/AuthWrapper';
import { apiService } from './services/apiService';
import { authService } from './services/authService';
import { ScenarioAnswers, Recommendation } from './types/camera';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState<'welcome' | 'questions' | 'recommendations'>('welcome');
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleStartQuestionnaire = () => {
    setCurrentView('questions');
    setError(null);
  };

  const handleQuestionnaireComplete = async (answers: ScenarioAnswers) => {
    setIsLoading(true);
    setError(null);

    try {
      const rec = await apiService.getCameraRecommendation(answers);
      setRecommendation(rec);
      setSelectedCamera(`${answers.cameraModel.brand} ${answers.cameraModel.model}`);
      setCurrentView('recommendations');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get recommendations. Please try again.';
      setError(errorMessage);
      console.error('Error getting recommendation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    setCurrentView('welcome');
    setRecommendation(null);
    setSelectedCamera(null);
    setError(null);
  };

  const handleAuthenticated = () => {
    // User is now authenticated
  };

  const handleLogout = () => {
    authService.logout();
    setCurrentView('welcome');
    setRecommendation(null);
    setError(null);
  };

  // Check if we're in testing mode
  const isTestingMode = process.env.REACT_APP_ENVIRONMENT !== 'production';


  return (
    <div className="App">
      <header className="App-header">
        <h1>📹 Action Camera Settings Advisor</h1>
        <p>Get personalized camera settings for your GoPro Hero 13, Hero 11, or DJI OSMO Action 5 Pro</p>
      </header>

      <main className="App-main">
        {isTestingMode ? (
          <>
            {/* TESTING MODE: Authentication bypassed for local development */}
            <div className="testing-banner">
              🧪 LOCAL TESTING MODE - Authentication Disabled
            </div>
            
            {error && (
              <div className="error-message">
                <p>{error}</p>
                <button onClick={() => setError(null)}>Dismiss</button>
              </div>
            )}

            {currentView === 'welcome' && (
              <div className="welcome-screen">
                <h2>Welcome to Your Personal Camera Assistant</h2>
                <p>
                  Answer a few quick questions about your filming scenario, and we'll provide 
                  you with optimized camera settings tailored to your specific needs.
                </p>
                <div className="supported-cameras">
                  <h3>Supported Cameras:</h3>
                  <ul>
                    <li>GoPro Hero 13</li>
                    <li>GoPro Hero 11</li>
                    <li>DJI OSMO Action 5 Pro</li>
                  </ul>
                </div>
                <button 
                  onClick={handleStartQuestionnaire} 
                  className="start-button"
                  disabled={isLoading}
                >
                  Get Started
                </button>
              </div>
            )}

            {currentView === 'questions' && (
              <QuestionFlow 
                onComplete={handleQuestionnaireComplete}
                isLoading={isLoading}
              />
            )}

            {currentView === 'recommendations' && recommendation && (
              <RecommendationDisplay
                recommendation={recommendation}
                cameraName={selectedCamera}
                onStartOver={handleStartOver}
              />
            )}

            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Getting your personalized recommendations...</p>
              </div>
            )}
          </>
        ) : (
          /* PRODUCTION MODE: Authentication required */
          <AuthWrapper onAuthenticated={handleAuthenticated}>
            {error && (
              <div className="error-message">
                <p>{error}</p>
                <button onClick={() => setError(null)}>Dismiss</button>
              </div>
            )}

            {currentView === 'welcome' && (
              <div className="welcome-screen">
                <h2>Welcome to Your Personal Camera Assistant</h2>
                <p>
                  Answer a few quick questions about your filming scenario, and we'll provide 
                  you with optimized camera settings tailored to your specific needs.
                </p>
                <div className="supported-cameras">
                  <h3>Supported Cameras:</h3>
                  <ul>
                    <li>GoPro Hero 13</li>
                    <li>GoPro Hero 11</li>
                    <li>DJI OSMO Action 5 Pro</li>
                  </ul>
                </div>
                <button 
                  onClick={handleStartQuestionnaire} 
                  className="start-button"
                  disabled={isLoading}
                >
                  Get Started
                </button>
              </div>
            )}

            {currentView === 'questions' && (
              <QuestionFlow 
                onComplete={handleQuestionnaireComplete}
                isLoading={isLoading}
                onLogout={handleLogout}
              />
            )}

            {currentView === 'recommendations' && recommendation && (
              <RecommendationDisplay
                recommendation={recommendation}
                cameraName={selectedCamera}
                onStartOver={handleStartOver}
              />
            )}

            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Getting your personalized recommendations...</p>
              </div>
            )}
          </AuthWrapper>
        )}
      </main>

      <footer className="App-footer">
        <p>Powered by AWS Bedrock and Claude AI</p>
      </footer>
    </div>
  );
}

export default App;
